import { useCallback, useRef, useState } from "react"
import { DialogContent } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { Form, FormikProps } from "formik"
import { Formik } from "../../forms/SchemaFormik"

import { useAppLocalization } from "../../../../modules/localization/components/AppLocalizationProvider"
import { generateBookingValidationSchema } from "../../createOrder/components/form/formLibBooking"
import {
    BookingConfig,
    DynamicConfigField,
    DynamicConfigKey,
    ExtendedDynamicConfigKey,
} from "../../createOrder/components/form/formTypes"
import { ResourceItem, SelectedMainUserState } from "../../createOrder/createOrderTypes"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { StandardDialog } from "../StandardDialog"
import { TimelineCalendarEventTimeRange } from "../../createOrder/components/resource/calendar/fullCalendarTypes"

import { AttendeesFormPart } from "./attendees/AttendeesFormPart"
import { MeetingDetailsFormPart } from "./meetingDetails/MeetingDetailsFormPart"
import { ConfirmBookingSkeleton } from "./components/ConfirmBookingSkeleton"

const useStyles = makeStyles()(() => ({
    dialogContent: {
        padding: "3rem",
        paddingTop: "1.5rem",
        display: "flex",
        gap: "3rem",
        flexDirection: "column",
        alignItems: "center",
    },
}))

type ConfirmBookingDialogProps = Readonly<{
    open: boolean
    onClose: (formValues?: Record<ExtendedDynamicConfigKey | string, any>) => void
    config?: BookingConfig
    initialValues?: Record<ExtendedDynamicConfigKey | string, any>
    handleSubmit: (values: Record<ExtendedDynamicConfigKey | string, any>) => void
    resourcesList: ResourceItem[]
    timeRange?: TimelineCalendarEventTimeRange
    handleClearResourceIds: () => void
    user: SelectedMainUserState
    orderId?: number | null
    isLoading?: boolean
    isLockedForOtherUser?: boolean
}>

export function ConfirmBookingDialog({
    open,
    onClose,
    config,
    initialValues,
    handleSubmit,
    resourcesList,
    timeRange,
    handleClearResourceIds,
    user,
    orderId,
    isLoading,
    isLockedForOtherUser,
}: ConfirmBookingDialogProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const [step, setStep] = useState(1)
    const formikRef = useRef<FormikProps<Record<DynamicConfigKey | string, any>>>(null)

    const handleCloseWithParams = () => {
        if (formikRef?.current) {
            const formValues = formikRef?.current?.values
            onClose(formValues)
            return
        }
        onClose()
    }

    const handleBackClick = () => setStep(1)

    const handleConfirmClick = useCallback(
        (isShowParticipants: boolean) => {
            isShowParticipants ? setStep(2) : formikRef.current?.submitForm()
        },
        [formikRef],
    )

    const step1Title = orderId ? (
        <LocalizedStrict id="create-order-confirm-booking-update-meeting-details" vars={{ id: `${orderId}` }}>
            <>Update meeting details #{orderId}</>
        </LocalizedStrict>
    ) : (
        <LocalizedStrict id="create-order-confirm-booking-meeting-details">
            <>Meeting details</>
        </LocalizedStrict>
    )

    const allFields: DynamicConfigField[] =
        config?.sections.flatMap((section) => section.groups.flatMap((group) => group.fields)) ?? []

    const shouldShowAttendeesStep =
        allFields.some((f) => f.key === DynamicConfigKey.ADD_PARTICIPANTS_TOGGLE) &&
        formikRef?.current?.values?.[DynamicConfigKey.ADD_PARTICIPANTS_TOGGLE] === true

    const validationSchema = config ? generateBookingValidationSchema(config, l10n) : undefined

    return (
        <StandardDialog
            dialogTitle={
                step === 1 ? (
                    step1Title
                ) : shouldShowAttendeesStep ? (
                    <LocalizedStrict id="create-order-confirm-booking-dialog-add-attendees">
                        <>Add participants</>
                    </LocalizedStrict>
                ) : undefined
            }
            open={open}
            onClose={handleCloseWithParams}
            maxWidthProp="lg"
            scroll="body"
            handleBackButton={step === 1 ? undefined : handleBackClick}
            backButtonText={l10n.getString("create-order-confirm-booking-dialog-back-button")}
        >
            {isLoading || !config || initialValues === undefined ? (
                <DialogContent className={classes.dialogContent}>
                    <ConfirmBookingSkeleton />
                </DialogContent>
            ) : (
                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    <Form>
                        <DialogContent className={classes.dialogContent}>
                            {step === 1 ? (
                                <MeetingDetailsFormPart
                                    config={config}
                                    handleClick={handleConfirmClick}
                                    resourcesList={resourcesList}
                                    timeRange={timeRange}
                                    handleClearResourceIds={handleClearResourceIds}
                                    user={user}
                                    isLockedForOtherUser={isLockedForOtherUser}
                                />
                            ) : shouldShowAttendeesStep ? (
                                <AttendeesFormPart />
                            ) : null}
                        </DialogContent>
                    </Form>
                </Formik>
            )}
        </StandardDialog>
    )
}
